import React, { useEffect, useState } from 'react';
import { ExternalLink, Loader } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchPublicEditorialContent } from '../services/editorialContentApi';

function youtubeEmbedUrl(url) {
  try {
    const u = new URL(url);
    let id = '';
    if (u.hostname.includes('youtu.be')) {
      id = u.pathname.replace(/^\//, '').split('/')[0];
    } else {
      const v = u.searchParams.get('v');
      if (v) id = v;
      const m = u.pathname.match(/\/embed\/([^/]+)/);
      if (m) id = m[1];
      const s = u.pathname.match(/\/shorts\/([^/]+)/);
      if (s) id = s[1];
    }
    if (!id) return null;
    return `https://www.youtube-nocookie.com/embed/${id}`;
  } catch {
    return null;
  }
}

export default function EditorialContentSection({ tag, title, emptyMessage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPublicEditorialContent(tag);
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tag]);

  if (loading) {
    return (
      <div className="flex justify-center py-8 text-gray-500">
        <Loader className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-4">
        {error}
      </p>
    );
  }

  if (!items.length) {
    return (
      <p className="text-gray-600 text-sm">
        {emptyMessage || 'Content coming soon. Follow us on social for daily plant inspiration.'}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {title && <h3 className="text-lg font-semibold text-gray-900">{title}</h3>}
      <ul className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
        {items.map((item) => {
          const embed = item.platform === 'youtube' ? youtubeEmbedUrl(item.url) : null;
          return (
            <li
              key={item.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
              {embed && (
                <div className="aspect-video bg-black">
                  <iframe
                    title={item.title || 'Video'}
                    src={embed}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    {item.contributor_label && (
                      item.designer_id ? (
                        <Link
                          to={`/find-designer?designer=${encodeURIComponent(item.designer_id)}`}
                          className="text-xs font-medium text-green-800 uppercase tracking-wide mb-1 hover:underline inline-block"
                        >
                          {item.contributor_label}
                        </Link>
                      ) : (
                        <p className="text-xs font-medium text-green-800 uppercase tracking-wide mb-1">
                          {item.contributor_label}
                        </p>
                      )
                    )}
                    <h4 className="font-semibold text-gray-900">
                      {item.title || (item.platform === 'instagram' ? 'Instagram' : 'Video')}
                    </h4>
                    {item.description && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-3">{item.description}</p>
                    )}
                  </div>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 p-2 text-green-700 hover:bg-green-50 rounded-lg"
                    aria-label="Open link"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
                {!embed && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    View on {item.platform === 'instagram' ? 'Instagram' : 'link'}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
